/**
 * @fileoverview Modal for adding a new manifest-only relationship to the dataset override.
 * @module system-web/components/migration/manifest-editor/AddRelationshipModal
 *
 * Responsibilities:
 * - Provide cascading dropdowns for selecting parent table / column and child table / column.
 * - Allow selection of cardinality and orphan policy.
 * - Require orphanPolicyJustification when orphan policy is not 'block'.
 * - Call onConfirm with a ManifestOnlyRelationship payload on submission.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useLegacySchema (fetchColumnsFor to populate column dropdowns)
 * - legacyManifestService (SchemaTable, SchemaColumn)
 *
 * Architecture:
 * - Overlay modal (fixed inset-0 bg-black/50) following the existing ConfirmRunModal pattern.
 * - Column dropdowns are populated by calling fetchColumnsFor on table selection (online exception).
 * - All form state is local; confirmed payload is bubbled to parent.
 *
 * Notes:
 * - enforcement is always 'manifest-only' for new relationships added through this modal.
 * - declaredAt / declaredBy are set by the parent before calling saveOverride (not in this modal).
 *
 * @reusable
 * Usage: Opened from the "Add Relationship" button in ManifestRelationshipsGrid.
 * Rules: stateful (form state, loading state); calls fetchColumnsFor (online exception).
 */

import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, Link2 } from 'lucide-react';
import type { SchemaTable, SchemaColumn } from '../../../services/legacyManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract AddRelationshipPayload
 *
 * Purpose:
 * - The output of the AddRelationshipModal upon confirmation.
 *
 * Fields:
 * - parentTable, parentColumn, childTable, childColumn: The four FK endpoints.
 * - cardinality: 'one-to-one' | 'one-to-many'
 * - orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot'
 * - orphanPolicyJustification: Required when orphanPolicy !== 'block'.
 *
 * Rules:
 * - Enforcement is always 'manifest-only' for payloads from this modal.
 *
 * Sync:
 * - Delivered to parent; parent merges into addedRelationships and calls saveOverride.
 */
export interface AddRelationshipPayload {
  parentTable: string;
  parentColumn: string;
  childTable: string;
  childColumn: string;
  cardinality: 'one-to-one' | 'one-to-many';
  orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification: string;
}

/**
 * @contract AddRelationshipModalProps
 *
 * Purpose:
 * - Props for the AddRelationshipModal component.
 *
 * Fields:
 * - tables: Live schema tables for the selected legacy database.
 * - fetchColumnsFor: Async function from useLegacySchema to get columns for a table.
 * - onConfirm: Called with the completed payload when the user submits.
 * - onCancel: Called when the user dismisses the modal.
 *
 * Rules:
 * - onConfirm may throw; the modal catches errors and shows them inline.
 *
 * Sync:
 * - Not applicable.
 */
export interface AddRelationshipModalProps {
  tables: SchemaTable[];
  fetchColumnsFor: (table: string) => Promise<SchemaColumn[]>;
  onConfirm: (payload: AddRelationshipPayload) => Promise<void>;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ORPHAN_POLICY_OPTIONS: { value: AddRelationshipPayload['orphanPolicy']; label: string; description: string }[] = [
  { value: 'block',          label: 'Block',         description: 'Fail the migration if orphans exist' },
  { value: 'warn',           label: 'Warn',          description: 'Log warnings; continue migration' },
  { value: 'drop',           label: 'Drop',          description: 'Silently skip orphaned child rows' },
  { value: 'includeAsRoot',  label: 'Include Root',  description: 'Migrate orphans as root documents' },
];

const CARDINALITY_OPTIONS: { value: AddRelationshipPayload['cardinality']; label: string }[] = [
  { value: 'one-to-many', label: 'One-to-Many (1:N)' },
  { value: 'one-to-one',  label: 'One-to-One (1:1)' },
];

// ---------------------------------------------------------------------------
// Column dropdown helper
// ---------------------------------------------------------------------------

interface ColumnSelectProps {
  table: string | null;
  columnsMap: Map<string, SchemaColumn[]>;
  value: string;
  onChange: (col: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  id: string;
}

function ColumnSelect({ table, columnsMap, value, onChange, isLoading, disabled, id }: ColumnSelectProps) {
  const cols = table ? columnsMap.get(table) ?? [] : [];
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || !table || isLoading}
        className="w-full rounded border px-2 py-1.5 text-sm appearance-none pr-7 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'rgba(0,0,0,0.18)' }}
      >
        <option value="">— Select column —</option>
        {cols.map((c) => (
          <option key={c.column} value={c.column}>
            {c.column}{c.isPrimaryKey ? ' (PK)' : ''} · {c.dataType}
          </option>
        ))}
      </select>
      {isLoading && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-gray-400" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component AddRelationshipModal
 *
 * Purpose:
 * - Full-screen overlay modal for adding a manifest-only relationship to the dataset override.
 *
 * Props:
 * - See AddRelationshipModalProps.
 *
 * Behavior:
 * - Parent table dropdown populated from tables prop (all live schema tables).
 * - Parent column dropdown loads via fetchColumnsFor when parent table is selected.
 * - Child table and column follow the same pattern independently.
 * - Cardinality selector (one-to-many / one-to-one).
 * - Orphan policy selector with description; justification textarea appears when non-block.
 * - Validate on submit: all four endpoints required; justification required for non-block.
 * - Calls onConfirm with the payload; shows errors inline if onConfirm throws.
 *
 * Data Source:
 * - remote (online-exception column fetch via fetchColumnsFor)
 *
 * Constraints:
 * - No Dexie reads or writes.
 *
 * Performance:
 * - Column fetches are cached in useLegacySchema.columnsCache; repeat opens are instant.
 *
 * @reusable
 * Usage: Opened by ManifestEditorTab on "Add Relationship" click.
 * Rules: stateful (full form state); delegates mutation to parent via onConfirm.
 */
export function AddRelationshipModal({
  tables,
  fetchColumnsFor,
  onConfirm,
  onCancel,
}: AddRelationshipModalProps) {
  const [parentTable, setParentTable] = useState('');
  const [parentColumn, setParentColumn] = useState('');
  const [childTable, setChildTable] = useState('');
  const [childColumn, setChildColumn] = useState('');
  const [cardinality, setCardinality] = useState<AddRelationshipPayload['cardinality']>('one-to-many');
  const [orphanPolicy, setOrphanPolicy] = useState<AddRelationshipPayload['orphanPolicy']>('block');
  const [justification, setJustification] = useState('');

  const [columnsMap, setColumnsMap] = useState<Map<string, SchemaColumn[]>>(new Map());
  const [loadingParentCols, setLoadingParentCols] = useState(false);
  const [loadingChildCols, setLoadingChildCols] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load parent columns on table change
  useEffect(() => {
    if (!parentTable) { setParentColumn(''); return; }
    setParentColumn('');
    if (columnsMap.has(parentTable)) return;
    setLoadingParentCols(true);
    fetchColumnsFor(parentTable)
      .then((cols) => {
        setColumnsMap((prev) => new Map(prev).set(parentTable, cols));
      })
      .catch((err) => {
        console.error('[AddRelationshipModal] fetchColumnsFor parent failed:', err);
      })
      .finally(() => setLoadingParentCols(false));
  }, [parentTable, fetchColumnsFor]); // columnsMap intentionally excluded (stale-on-purpose cache)

  // Load child columns on table change
  useEffect(() => {
    if (!childTable) { setChildColumn(''); return; }
    setChildColumn('');
    if (columnsMap.has(childTable)) return;
    setLoadingChildCols(true);
    fetchColumnsFor(childTable)
      .then((cols) => {
        setColumnsMap((prev) => new Map(prev).set(childTable, cols));
      })
      .catch((err) => {
        console.error('[AddRelationshipModal] fetchColumnsFor child failed:', err);
      })
      .finally(() => setLoadingChildCols(false));
  }, [childTable, fetchColumnsFor]); // columnsMap intentionally excluded

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!parentTable)  errs.parentTable  = 'Select the parent table.';
    if (!parentColumn) errs.parentColumn = 'Select the parent column.';
    if (!childTable)   errs.childTable   = 'Select the child table.';
    if (!childColumn)  errs.childColumn  = 'Select the child column.';
    if (parentTable && childTable && parentTable === childTable) {
      errs.childTable = 'Parent and child tables must differ.';
    }
    if (orphanPolicy !== 'block' && !justification.trim()) {
      errs.justification = 'Justification is required for non-block orphan policies.';
    }
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        parentTable,
        parentColumn,
        childTable,
        childColumn,
        cardinality,
        orphanPolicy,
        orphanPolicyJustification: justification.trim(),
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save relationship');
      setIsSubmitting(false);
    }
  }

  const fieldCls = (field: string) =>
    `w-full rounded border px-2 py-1.5 text-sm ${validationErrors[field] ? 'border-red-400' : ''}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-gray-500" />
            <h2 className="text-base font-semibold">Add Manifest-Only Relationship</h2>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* Parent endpoint */}
          <fieldset className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <legend className="text-xs font-semibold uppercase tracking-wide px-1 text-gray-500">Parent (referenced)</legend>
            <div>
              <label htmlFor="parentTable" className="block text-xs font-medium mb-1">Table</label>
              <select
                id="parentTable"
                value={parentTable}
                onChange={(e) => setParentTable(e.target.value)}
                className={fieldCls('parentTable')}
                style={{ borderColor: validationErrors.parentTable ? undefined : 'rgba(0,0,0,0.18)' }}
              >
                <option value="">— Select table —</option>
                {tables.map((t) => (
                  <option key={`${t.schema}.${t.table}`} value={t.table}>{t.schema}.{t.table}</option>
                ))}
              </select>
              {validationErrors.parentTable && <p className="text-xs text-red-600 mt-0.5">{validationErrors.parentTable}</p>}
            </div>
            <div>
              <label htmlFor="parentColumn" className="block text-xs font-medium mb-1">Column (FK target)</label>
              <ColumnSelect
                id="parentColumn"
                table={parentTable}
                columnsMap={columnsMap}
                value={parentColumn}
                onChange={setParentColumn}
                isLoading={loadingParentCols}
              />
              {validationErrors.parentColumn && <p className="text-xs text-red-600 mt-0.5">{validationErrors.parentColumn}</p>}
            </div>
          </fieldset>

          {/* Child endpoint */}
          <fieldset className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <legend className="text-xs font-semibold uppercase tracking-wide px-1 text-gray-500">Child (referencing)</legend>
            <div>
              <label htmlFor="childTable" className="block text-xs font-medium mb-1">Table</label>
              <select
                id="childTable"
                value={childTable}
                onChange={(e) => setChildTable(e.target.value)}
                className={fieldCls('childTable')}
                style={{ borderColor: validationErrors.childTable ? undefined : 'rgba(0,0,0,0.18)' }}
              >
                <option value="">— Select table —</option>
                {tables.map((t) => (
                  <option key={`${t.schema}.${t.table}`} value={t.table}>{t.schema}.{t.table}</option>
                ))}
              </select>
              {validationErrors.childTable && <p className="text-xs text-red-600 mt-0.5">{validationErrors.childTable}</p>}
            </div>
            <div>
              <label htmlFor="childColumn" className="block text-xs font-medium mb-1">Column (FK source)</label>
              <ColumnSelect
                id="childColumn"
                table={childTable}
                columnsMap={columnsMap}
                value={childColumn}
                onChange={setChildColumn}
                isLoading={loadingChildCols}
              />
              {validationErrors.childColumn && <p className="text-xs text-red-600 mt-0.5">{validationErrors.childColumn}</p>}
            </div>
          </fieldset>

          {/* Cardinality */}
          <div>
            <label htmlFor="cardinality" className="block text-xs font-medium mb-1">Cardinality</label>
            <select
              id="cardinality"
              value={cardinality}
              onChange={(e) => setCardinality(e.target.value as typeof cardinality)}
              className="w-full rounded border px-2 py-1.5 text-sm"
              style={{ borderColor: 'rgba(0,0,0,0.18)' }}
            >
              {CARDINALITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Orphan policy */}
          <div>
            <label htmlFor="orphanPolicy" className="block text-xs font-medium mb-1">Orphan Policy</label>
            <select
              id="orphanPolicy"
              value={orphanPolicy}
              onChange={(e) => setOrphanPolicy(e.target.value as typeof orphanPolicy)}
              className="w-full rounded border px-2 py-1.5 text-sm"
              style={{ borderColor: 'rgba(0,0,0,0.18)' }}
            >
              {ORPHAN_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} — {o.description}</option>
              ))}
            </select>
          </div>

          {/* Justification (required when non-block) */}
          {orphanPolicy !== 'block' && (
            <div>
              <label htmlFor="justification" className="block text-xs font-medium mb-1">
                Orphan Policy Justification <span className="text-red-500">*</span>
              </label>
              <textarea
                id="justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                className={`${fieldCls('justification')} resize-none`}
                style={{ borderColor: validationErrors.justification ? undefined : 'rgba(0,0,0,0.18)' }}
                placeholder="Explain why this orphan policy is safe for migration."
              />
              {validationErrors.justification && (
                <p className="text-xs text-red-600 mt-0.5">{validationErrors.justification}</p>
              )}
            </div>
          )}

          {/* Submit error */}
          {submitError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end px-5 py-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
            style={{ background: '#2563eb' }}
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add Relationship
          </button>
        </div>
      </div>
    </div>
  );
}
